import { gql } from "@apollo/client"
import craftApolloClient from "./api/apollo"

import { useState, useEffect } from "react"
import { useRouter } from "next/router"
import Head from "next/head"

import { format } from 'date-fns'
import { formatInTimeZone } from 'date-fns-tz'

import _ from 'lodash'
import { toHoursAndMinutes } from "../lib/agenda-utils"

import { Header, HeaderShim } from '../fuselage/global/header/header'
import { Session, SessionSpeaker, SessionSpeakerGrid } from '../fuselage/components/session/session'
import { Modal } from "../fuselage/components/modal/modal"
import { Sidebar } from "../fuselage/layout/sidebar/sidebar"
import { Avatar } from '../fuselage/components/avatar/avatar'
import { Button } from "../fuselage/components/button/button"

import SimpleBar from "simplebar-react"
import 'simplebar-react/dist/simplebar.min.css'

import CSS from '../css/app/agenda.module.css'


export default function Agenda ({ agendaSettings, sessions, speakers, speakersOverflow, cmsAgendaData }) {

    const [ currentFilter, setCurrentFilter ] = useState (null)
    const [ currentDay, setCurrentDay ] = useState (null)
    const [ currentStage, setCurrentStage ] = useState(null)
    const [ showModal, setShowModal ] = useState(false)
    const [ modalContent, setModalContent ] = useState({ avatar: '', name: '', title: '', company: '', bio: ''})

    const router = useRouter()

    useEffect( () => {
        if ( router.query.filter ) {
            setCurrentFilter(parseInt(router.query.filter))
        }
    
        if ( router.query.day ) {
            setCurrentDay(parseInt(router.query.day) - 1)
        }
    
    }, [ router ])
    

    // 63171 = Hall A
    // 63172 = Hall B
    // 63173 = Exhibition area
    // 63174 = Hall A&B

    
    let mainStreamIds = [ 63171, 63172, 63173, 63174 ]

    // check for global (and untagged) sessions and dump in array

    let globalSessions = ( findGlobalBzSessions( sessions, mainStreamIds ) )

    
    // cms entry data

    const entry = cmsAgendaData.data.entry
    const sessionThemes = entry.bizzaboAgendaTheme

    let metaTitle = JSON.parse(entry.seomatic.metaTitleContainer)
    let metaTags = JSON.parse(entry.seomatic.metaTagContainer)  

    
    
    // handle 200+ speakers ( requires separate api requests )

    if ( speakersOverflow && speakersOverflow.content ) {
        speakers.content = [].concat( speakers.content, speakersOverflow.content )
    }
    
  

    
    // console.log('agenda data (CMS):', cmsAgendaData)
    // console.log('settings:', agendaSettings)
    // console.log('sessions:', sessions)
    // console.log('speakers:', speakers.content)
    // console.log('speakers found:', speakers.content.length)
    // console.log('combined speakers:', speakers)




    // build agenda

    function outputAgenda ( sessions ) {

        let agenda = groupSessionsIntoDays(sessions)

        // console.log('agenda:', agenda)

        if ( currentDay !== null ) {
            // output just one day
            return (
                <>
                    { renderDivider( agenda[currentDay][0].startDate ) }
                    { renderSessions( agenda[currentDay] ) }
                </>
            )

        } else {
            // output all days
            return (
                agenda.map( (day, index) => (
                    <div key={index} className='mb-xs'>
                        { renderDivider( day[0].startDate ) }
                        { renderSessions( day ) }
                    </div>
                ))
            )
            
        }



        // helpers

        function renderDivider ( day ) {
            return (
                <p className='fs-4 fw-600 c-white bg-primary px-sm py-xs mb-xs'>
                    { day && formatInTimeZone(new Date( day ), 'Europe/Prague', 'EEEE dd MMMM') }
                </p>
            )
        }

        function renderSessions ( sessions ) {

            // filter sessions (by track)
            if ( currentFilter ) {
                let filteredSessionsByTrack = checkForMainBzSessions( sessions, currentFilter )
                sessions = filteredSessionsByTrack
            }

            // filter sessions (by stage)
            if ( currentStage ) {
                let filteredSessionsByStage = checkForMainBzSessions( sessions, currentStage )
                sessions = _.intersection(sessions, filteredSessionsByStage)
            }

            // render sessions
            if ( sessions.length ) {
                return (
                    sessions.map( session => {

                        let sessionTags = []
    
                        if ( session.filters ) {
                            session.filters.forEach( filter => {
                                filter.tags.forEach( tag =>  sessionTags.push(getSessionTagTitle(tag)) )
                            })
                        }

                        
                        return (
                           

                                <Session
                                    key={session.id}
                                    id={session.id}
                                    startTime={toHoursAndMinutes(session.startMinute)}
                                    endTime={toHoursAndMinutes(session.endMinute)}
                                    tags={sessionTags}
                                    theme={getSessionTheme(sessionTags)}
                                >   
                                    <p className='h3 c-primary'>{ session.title }</p>
                                    
                                    {
                                        session.descriptionHtml &&
                                        <div dangerouslySetInnerHTML={{__html: session.descriptionHtml}} className="mb-xs formatted" />
                                    }

                                    {addSessionCta( session.id )}

                                    <div>{getAllSessionSpeakers(session.speakers)}</div>
        
                                </Session>
                           
                        )
                    })
                )

            
            } else {
                return <p className="fs-6 fw-400">No sessions available.</p>
            }
            
        }    

        function getAllSessionSpeakers( speakerIds ) {
            
            if ( !speakerIds ) return

            let moderators = []
            let profiles = []

            speakerIds.forEach( speaker => {
                const speakerProfile = speakers.content.find( obj => obj.id === speaker.speakerId )

                if ( speakerProfile ) {
                    const profile = new SessionSpeakerProfile( 
                        speakerProfile.firstname, 
                        speakerProfile.lastname, 
                        speakerProfile.title,
                        speakerProfile.company,
                        speakerProfile.photoSet ? speakerProfile.photoSet.large : null,
                        speaker.role ? speaker.role : null,
                        speakerProfile.bio 
                    )
                    profile.role === 'Moderator' ? moderators.push( profile ) : profiles.push( profile )
                }
            })

            return (
                <>
                    {
                        moderators.length ?
                            <div>
                                <SessionSpeakerGrid>
                                    { moderators.map( (moderator, index) => handleSpeakerCard( moderator, index) ) }
                                </SessionSpeakerGrid>
                                <p className="fw-500 my-xs c-primary">In conversation with</p>
                            </div>
                        : null
                    }
                    <SessionSpeakerGrid>
                        { profiles.map( (profile, index) => handleSpeakerCard( profile, index) ) }
                    </SessionSpeakerGrid>

                    
                </>
            )

        }

        function addSessionCta( id ) {

            const selectedSessions = [
                { id: 968557, href: "/ae/site-tours#farm", target: '' },
                { id: 968558, href: "/ae/site-tours#lagoon", target: '' }
            ]

            let selectedSession = selectedSessions.find( session => session.id === id)
            let sessionDetails = sessions.find( session => session.id === id)
            
            if ( selectedSession ) {
                return <Button href={selectedSession.href} size={`small`} decal={`fs-sm fw-400 mb-xs no-print`} target={selectedSession.target}>
                    Learn more and register
                </Button>
            }
        }

        function handleSpeakerCard( profile, index) {
            return (
                <span
                    key={index} 
                    onClick={() => {
                        if ( profile.bio ) {
                            setModalContent({
                                avatar: profile.avatarUrl,
                                name: `${profile.firstname} ${profile.lastname}`,
                                title: profile.title,
                                company: profile.company,
                                bio: profile.bio
                            })
                            setShowModal(true)
                        }
                    }}>
                    <SessionSpeaker 
                        avatarUrl={profile.avatarUrl}
                        role={profile.role}
                        firstname={profile.firstname}
                        lastname={profile.lastname}
                        title={profile.title}
                        company={profile.company}
                        bio={profile.bio}
                    />
                </span>
            )
        }

    }


    class SessionSpeakerProfile {
        constructor( firstname, lastname, title, company, avatarUrl, role, bio ) {
            this.firstname = firstname
            this.lastname = lastname
            this.title = title
            this.company = company
            this.avatarUrl = avatarUrl
            this.role = role
            this.bio = bio
        }
    }
   
    function groupSessionsIntoDays ( sessions ) {

        const orderedSessions = _.orderBy(sessions, [ 'startMinute', 'asc'])
        // console.log('orderedSessions:', orderedSessions)
    
        const groupedSessions = _
            .chain(orderedSessions)
            .groupBy(function(session) { return new Date(session.startDate).getDay() })
            .sortBy(function(session) { return session.startMinute })
            .value()
        // console.log('sorted sessions:', groupedSessions)
        // console.log('sorted sessions [1]:', groupedSessions[1])
    
        return groupedSessions
    }


    // tags

    let mergedTags = []

    if ( agendaSettings && agendaSettings.filters ) {
        agendaSettings.filters.forEach( obj => {
            obj.tags.forEach( tag => mergedTags.push(tag))
        })
    }

    const getSessionTagTitle = ( id ) => {
        let tag = mergedTags.filter( tag => tag.id == id )
        if ( tag.length ) return tag[0].name
        return
    }

    
    // session theme

    const getSessionTheme = ( bizzaboAgendaTags ) => {

        // check if a cms sessionThemes object exists for bz tag
        const found = sessionThemes.filter( obj => bizzaboAgendaTags.includes( obj.bizzaboAgendaTag ))

        // if yes, return the theme color
        if ( found.length ) return found[0].color
        
        // otherwise return a fallback
        return 'var(--accent)'
    }

    
    // filter query reset

    function clearQuery() {
        router.replace('/agenda', undefined, { shallow: true })
    }


    // check bz agenda settings data for specific tag group

    function searchBzFilters( filterData, targetFilterName ) {

        if ( !filterData || !targetFilterName ) return

        let isolatedFilter = []

        filterData.some( arr => {
            if ( arr.name === targetFilterName ) isolatedFilter = arr.tags
        })
        
        return isolatedFilter
    }


    // find global sessions ( to be included in all main streams )
    
    function findGlobalBzSessions ( sessionIds, streamIds ) {

        if ( !sessionIds ) return
        
        let globalSessionIds = []
    
        sessionIds.forEach( session => {
            // check for filters
            if (session.filters) {                
                session.filters.forEach( filter => {
                    // check for tags
                    if (filter.tags) {
                        // check for main stream tags
                        // if no main stream tags are found, push into array
                        if ( !_.intersection(filter.tags, streamIds) ) globalSessionIds.push(session)
                    }
                })
            }
    
            if (!session.filters) {
                // if no filters + tags are found, push into array
                globalSessionIds.push(session)
            }
        })

        // console.log('global sessions: ', globalSessionIds)
        
        return globalSessionIds
        
    }


    // check if bz filter is allocated as main (for filtering)

    function checkForMainBzSessions ( bxSessions, bxFilterId ) {
        let snapshot = bxSessions.filter( session => {
            // for sessions with filter + tags
            if (session.filters) {
                if ( 
                    session.filters[0].tags.includes(parseInt(bxFilterId)) || 
                    session.filters[1] && session.filters[1].tags.includes(parseInt(bxFilterId)) ||
                    globalSessions && globalSessions.includes(session)
                ) return session
            } 
            // for sessions without filters
            else if ( globalSessions && globalSessions.includes(session) ) {
                // to include non-stream session, return just the session
                // return session
                // to exclude non-stream sessions, return nothing
                return
            }
            
        })
        return snapshot
    }
    


  


    return (
        <>

            <Head>
				<title>{metaTitle.title.title}</title>

				<meta name="description" content={metaTags.description.content} />
				<meta name="referrer" content={metaTags.referrer.content} />
				<meta content={metaTags['og:locale'].content} property="og:locale" />
				<meta content={metaTags['og:site_name'].content} property="og:site_name" />
				<meta content={metaTags['og:type'].content} property="og:type" />
				<meta content={metaTags['og:url'].content} property="og:url" />
				<meta content={metaTags['og:title'].content} property="og:title" />
				<meta content={metaTags['og:description'].content} property="og:description" />
				<meta content={metaTags['og:image'].content} property="og:image"></meta>  
			</Head>

            <HeaderShim />


            <section className="block">
                <div className="container">
                    <p className="fs-0 fw-600 c-primary">2023 Agenda</p>
                    
                    <div className={CSS.filterLayout}>

                        {/* track buttons */}
                        {/* <section className={CSS.filterLayoutButtons}>
                            
                            <button 
                                className={`${CSS.filterButton} ${ currentFilter === 63171 ? CSS.active : null }`} 
                                onClick={ () => {
                                    setCurrentFilter(63171)
                                    clearQuery()
                                }}
                            >Hall A</button>

                            <button 
                                className={`${CSS.filterButton} ${ currentFilter === 63172 ? CSS.active : null }`} 
                                onClick={ () => {
                                    setCurrentFilter(63172)
                                    clearQuery()
                                }}
                            >Hall B</button>

                            <button 
                                className={`${CSS.filterButton} ${ currentFilter === 63173 ? CSS.active : null }`} 
                                onClick={ () => {
                                    setCurrentFilter(63173)
                                    clearQuery()
                                }}
                            >Exhibition area</button>
                            
                            <button 
                                className={`${CSS.filterButton} ${ currentFilter === 63174 ? CSS.active : null }`} 
                                onClick={ () => {
                                    setCurrentFilter(63174)
                                    clearQuery()
                                }}
                            >Hall A&amp;B</button>

                            <button 
                                className={`${CSS.filterButton} ${ currentFilter === null ? CSS.active : null }`} 
                                onClick={ () => {
                                    setCurrentFilter(null)
                                    clearQuery()
                                }}
                            >All</button>

                        </section> */}


                        {/* day buttons */}
                        <section className={CSS.filterLayoutButtons}>
                            {
                                groupSessionsIntoDays(sessions).map( (day, index) => {
                                    return (
                                        <button 
                                            className={`
                                                ${CSS.filterButton} 
                                                ${ currentDay === index ? CSS.active : null }
                                            `}
                                            key={index} 
                                            onClick={ () => {
                                                setCurrentDay(index)
                                                clearQuery()
                                            }}
                                        >Day { index + 1 }</button>
                                    )
                                })
                            }
                            <button 
                                className={`${CSS.filterButton} ${ currentDay === null ? CSS.active : null }`} 
                                onClick={ () => {
                                    setCurrentDay(null)
                                    clearQuery()
                                }}
                            >All</button>

                        </section>

                        {/* stage select dropdown */}
                        <section className={CSS.filterLayoutButtons}>
                            {/* <p className={`${CSS.desktopOnly} fw-400 c-primary`}>Filter:&emsp;</p> */}
                            <select
                                className={CSS.filterSelect} 
                                defaultValue=""
                                onChange={ (e) => {
                                    setCurrentStage( parseInt(e.target.value) )
                                    clearQuery()
                                    // console.log('selected filter:', e.target.value)
                                }}
                            >
                                <option value="">All locations</option>
                                {
                                    agendaSettings.filters &&
                                    searchBzFilters( agendaSettings.filters, 'Location').map( filter => {
                                        return (
                                            <option key={filter.id} value={filter.id}>
                                                {filter.name}
                                            </option>
                                        )
                                    })
                                }
                            </select>
                        </section> 
  
                    </div>
                </div>

                <div className="container">
                    {outputAgenda( sessions )}
                </div>
            </section>


            <Modal onClose={() => setShowModal(false)} show={showModal}>
                <Sidebar size={`medium`}>
                    <aside className="center">
                        {
                            modalContent.avatar ?
                                <Avatar src={modalContent.avatar}/>
                                :
                                <Avatar src={`/assets/ui/avatar-fallback.png`}/>
                        }
                        <p className="fs-5 fw-600 c-primary mt-sm lh-1">{ modalContent.name }</p>
                        <p className="fs-sm fw-400 mt-xs">{modalContent.title} <br/>{modalContent.company}</p>
                    </aside>
                    
                    <SimpleBar style={{ maxHeight: 300 }}>
                        <div className="formatted pr-xs" dangerouslySetInnerHTML={{ __html: modalContent.bio }}></div>
                    </SimpleBar>                            
                    
                </Sidebar>
            </Modal>

        </>
    )
}


export async function getStaticProps() {

    // cms (theme etc)
    const cmsAgendaData = await craftApolloClient().query({
        query: gql`
			query SEO {
				entry(site: "${process.env.SITE_HANDLE}", id: "22403") {
					title
                    ... on ${process.env.SITE_HANDLE}Agenda_${process.env.SITE_HANDLE}Agenda_Entry {
                        id
                        bizzaboAgendaTheme {
                            ... on bizzaboAgendaTheme_BlockType {
                                id
                                color
                                bizzaboAgendaTag
                            }
                        }
                    }
					seomatic (asArray: true) {
						metaTitleContainer
						metaTagContainer
					}
				}
			}
        `
    })


    // sessions
    const sessionsRes = await fetch(`https://api.bizzabo.com/api/agenda/sessions?eventId=${process.env.BIZZABO_EVENT_ID}&size=200`, {
		"method": "GET",
		"headers": {
			"Content-Type": "application/json",
			"Authorization": "Bearer 67f61f9e-e897-4536-914e-3f47286650f9"
		}
	})
    const sessionsData = await sessionsRes.json()
    const sessions = sessionsData.content
    

    // settings
    const agendaSettingsRes = await fetch(`https://api.bizzabo.com/api/agenda/settings?eventId=${process.env.BIZZABO_EVENT_ID}`, {
		"method": "GET",
		"headers": {
			"Content-Type": "application/json",
			"Authorization": "Bearer 67f61f9e-e897-4536-914e-3f47286650f9"
		}
	})
    const agendaSettings = await agendaSettingsRes.json()


    // speakers
    const bizzaboSpeakers = await fetch(`https://api.bizzabo.com/api/speakers?eventId=${process.env.BIZZABO_EVENT_ID}&size=200`, {
		"method": "GET",
		"headers": {
			"Content-Type": "application/json",
			"Authorization": "Bearer 67f61f9e-e897-4536-914e-3f47286650f9"
		}
	})
	const speakers = await bizzaboSpeakers.json()
  


    return { props: 
        { 
            sessions, 
            agendaSettings, 
            speakers, 
            cmsAgendaData 
        } 
    }
}

